/**
 * The sprite contract: which sounds exist, and where each one sits in the one audio file.
 *
 * This module is the single source of truth for the key list, imported by both the runtime and
 * `scripts/gen-sounds.mts`. A generator with its own copy of the list is a generator that can
 * silently stop producing a sound the player still asks for — the failure is a missing groan on
 * the deployed page, which nothing in a build catches.
 *
 * Deliberately free of DOM and of Node: it is imported by a browser module, by a build script and
 * by tests, and the only thing all three agree on is arithmetic.
 */

/**
 * Sprite order is the order in the file. F24 may re-voice any of these, but the *keys* are the
 * hand-off contract with that task and must not be renamed.
 */
export const SPRITE_NAMES = [
  'groan_low',
  'groan_high',
  'thud_soft',
  'thud_wall',
  'gate_crash',
  'rebuild_hammer',
  'cheer',
  'reset_fanfare',
  'ambient_day',
  'ambient_siege',
] as const

export type SpriteName = (typeof SPRITE_NAMES)[number]

/** `[start, duration]` in seconds, exactly as `public/sounds/sprite.json` stores it. */
export type SpriteRegion = readonly [start: number, duration: number]

export type SpriteMap = Record<string, SpriteRegion>

/** The two beds, which are the only regions played with `loop = true`. */
export const AMBIENT_NAMES = ['ambient_day', 'ambient_siege'] as const

export type AmbientName = (typeof AMBIENT_NAMES)[number]

export type Bed = 'day' | 'siege' | 'none'

export function isAmbient(name: SpriteName): name is AmbientName {
  return name === 'ambient_day' || name === 'ambient_siege'
}

/**
 * Validate a decoded `sprite.json`.
 *
 * The offsets are fetched at runtime rather than compiled in, so a sprite regenerated with one
 * fewer region would otherwise reach the player as `undefined` and throw inside the first
 * `BufferSource.start` — in a gesture handler, where the stack says nothing about audio. Failing
 * here names the missing key instead.
 */
export function parseSpriteMap(value: unknown): SpriteMap {
  if (typeof value !== 'object' || value === null) {
    throw new Error('sprite.json is not an object')
  }
  const raw = value as Record<string, unknown>
  const map: Record<string, SpriteRegion> = {}
  for (const name of SPRITE_NAMES) {
    const region = raw[name]
    if (!Array.isArray(region) || region.length !== 2) {
      throw new Error(`sprite.json: "${name}" is missing or is not a [start, duration] pair`)
    }
    const [start, duration] = region as unknown[]
    if (typeof start !== 'number' || typeof duration !== 'number' || duration <= 0) {
      throw new Error(`sprite.json: "${name}" has a non-numeric or empty region`)
    }
    map[name] = [start, duration]
  }
  return map
}
